import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layers3, Sparkles, FileStack, Users, Settings, LogOut, Languages, ExternalLink, ShieldCheck, User as UserIcon, Newspaper, ScrollText, Inbox, Cpu, Clapperboard } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import GenerateTab from "./GenerateTab";
import DraftsTab from "./DraftsTab";
import PublishedTab from "./PublishedTab";
import UsersTab from "./UsersTab";
import SettingsTab from "./SettingsTab";
import LogsTab from "./LogsTab";
import InboxTab from "./InboxTab";
import AIProvidersTab from "./AIProvidersTab";
import DubbingTab from "./DubbingTab";

export default function DashboardPage() {
  const { user, isAdmin, logout } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const can = (f) => isAdmin || (user?.permissions || []).includes(f);
  const [tab, setTab] = useState(() => (can("generate") ? "generate" : can("dubbing") ? "dubbing" : "none"));
  const [unread, setUnread] = useState(0);
  useDocumentTitle(t("Painel · ReiMultiBlog", "Dashboard · ReiMultiBlog"));

  const loadUnread = useCallback(() => {
    if (!isAdmin) return;
    api.get("/admin/submissions/counts").then((r) => setUnread(r.data?.unread || 0)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { loadUnread(); }, [loadUnread, tab]);

  const tabs = [
    { id: "generate", label: t("Gerar Conteúdo", "Generate Content"), icon: Sparkles, show: can("generate") },
    { id: "drafts", label: "Drafts", icon: FileStack, show: can("generate") },
    { id: "dubbing", label: t("Dobragem", "Dubbing"), icon: Clapperboard, show: can("dubbing") },
    { id: "published", label: t("Publicados", "Published"), icon: Newspaper, show: isAdmin },
    { id: "inbox", label: t("Mensagens", "Inbox"), icon: Inbox, show: isAdmin, badge: unread },
    { id: "users", label: t("Utilizadores", "Users"), icon: Users, show: isAdmin },
    { id: "settings", label: t("Configurações", "Settings"), icon: Settings, show: isAdmin },
    { id: "ai", label: "IA", icon: Cpu, show: isAdmin },
    { id: "logs", label: "Logs", icon: ScrollText, show: isAdmin },
  ].filter((x) => x.show);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-background/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-xl"><img src="/logo.png" alt="ReiMultiBlog" className="h-9 w-9 object-cover" /></span>
              <span className="font-heading text-lg font-extrabold hidden sm:block">Rei<span className="text-primary">MultiBlog</span></span>
            </Link>
            <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold flex items-center gap-1.5">
              {isAdmin ? <ShieldCheck className="h-3.5 w-3.5 text-primary" /> : <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />}
              {isAdmin ? "Admin" : t("Convidado", "Invited")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggle} data-testid="admin-lang-toggle" className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5">
              <Languages className="h-4 w-4" />{lang.toUpperCase()}
            </button>
            <a href="/" target="_blank" rel="noreferrer" className="rounded-lg px-2.5 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 hidden sm:flex items-center gap-1.5">
              <ExternalLink className="h-4 w-4" />{t("Ver site", "View site")}
            </a>
            <span className="text-sm text-muted-foreground hidden md:block">{user?.name || user?.email}</span>
            <Button data-testid="logout-btn" variant="secondary" onClick={doLogout} className="gap-2"><LogOut className="h-4 w-4" />{t("Sair", "Logout")}</Button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              data-testid={`tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === tb.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <tb.icon className="h-4 w-4" />{tb.label}
              {tb.badge > 0 && <span data-testid="inbox-badge" className="ml-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 leading-none">{tb.badge}</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        {tabs.length === 0 && <p data-testid="no-access" className="text-muted-foreground">{t("Ainda não tem acesso a nenhuma funcionalidade. Peça ao administrador.", "You don't have access to any feature yet. Ask the administrator.")}</p>}
        {tab === "dubbing" && can("dubbing") && <DubbingTab />}
        {tab === "generate" && can("generate") && <GenerateTab onGenerated={() => setTab("drafts")} />}
        {tab === "drafts" && can("generate") && <DraftsTab />}
        {tab === "published" && isAdmin && <PublishedTab />}
        {tab === "inbox" && isAdmin && <InboxTab onChanged={loadUnread} />}
        {tab === "users" && isAdmin && <UsersTab />}
        {tab === "settings" && isAdmin && <SettingsTab />}
        {tab === "ai" && isAdmin && <AIProvidersTab />}
        {tab === "logs" && isAdmin && <LogsTab />}
      </main>
    </div>
  );
}
